import { isStrictAdmin, isSuperAdmin } from "@/helpers/UserHelpers";
import { createSlice, isRejectedWithValue } from "@reduxjs/toolkit";
import type { Middleware, PayloadAction, ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import type { QueryCacheKey } from "@reduxjs/toolkit/query";
import type { ConversationView, CursorPage } from "@/interfaces/Chat";
import { chatApi, ChatConversationArgs, ChatConversationScope } from "./chat";
import { resetCredentials, setCredentials } from "./slices/authSlice";

export type ChatSessionState = {
  generation: number;
  meetAdminVersion: number;
  refreshVersion: number;
  blocked: boolean;
  conversationVersions: Record<string, number>;
  conversationListVersions: Record<ChatConversationScope, number>;
};
const initialState: ChatSessionState = {
  generation: 0, meetAdminVersion: 0, refreshVersion: 0, blocked: false, conversationVersions: {},
  conversationListVersions: { mine: 0, others: 0 },
};
const session = createSlice({
  name: "chatSession",
  initialState,
  reducers: {
    purgeChatMeetAdmin: (state, _action: PayloadAction<{ actorId: string }>) => { state.meetAdminVersion += 1; },
    refreshChat: (state) => { state.refreshVersion += 1; },
    clearChatSession: (state, { payload }: PayloadAction<{ actorId?: string; blocked?: boolean }>) => {
      state.generation += 1;
      state.blocked = payload.blocked ?? false;
      state.conversationVersions = {};
      state.conversationListVersions = { mine: 0, others: 0 };
    },
    purgeChatConversationList: (state, { payload }: PayloadAction<{ actorId: string; scope: ChatConversationScope }>) => {
      state.conversationListVersions[payload.scope] += 1;
    },
    purgeChatConversation: (state, { payload }: PayloadAction<ChatConversationArgs>) => {
      const id = payload.conversationId;
      state.conversationVersions[id] = (state.conversationVersions[id] ?? 0) + 1;
    },
  },
});
export const { purgeChatMeetAdmin, refreshChat, clearChatSession, purgeChatConversation, purgeChatConversationList } = session.actions;
export const chatSessionReducer = session.reducer;

type State = {
  auth: { user: { id: string; roles?: string[] } | null; token: string | null };
  chatApi: ReturnType<typeof chatApi.reducer>;
  chatSession: ChatSessionState;
};
type CachedArgs = { actorId: string; conversationId?: string; scope?: ChatConversationScope };
const isChatArgs = (args: unknown): args is CachedArgs =>
  !!args && typeof args === "object" && typeof (args as CachedArgs).actorId === "string";
const meetAdminEndpoints = new Set(["getMeetConnection", "connectMeet", "disconnectMeet"]);
const conversationReads = new Set(["getChatConversation", "getChatMembers", "getChatMessages", "getChatChanges"]);

/** Owns chat lifetimes only; never resets the main business/calendar API. */
export const chatSessionMiddleware: Middleware<{}, State> = (store) => (next) => (action) => {
  const before = store.getState();
  const dispatch = store.dispatch as ThunkDispatch<State, unknown, UnknownAction>;
  const clearing = clearChatSession.match(action);
  const purging = purgeChatConversation.match(action);
  const purgingMeetAdmin = purgeChatMeetAdmin.match(action);
  const purgingList = purgeChatConversationList.match(action);
  // An unmount or response from a previous actor cannot clear the current actor.
  if ((clearing || purging || purgingList || purgingMeetAdmin) && action.payload.actorId && action.payload.actorId !== before.auth.user?.id) return action;
  const result = next(action);
  const current = store.getState();
  if (setCredentials.match(action) || resetCredentials.match(action)) {
    if (before.auth.user?.id !== current.auth.user?.id || before.auth.token !== current.auth.token) {
      dispatch(clearChatSession({ actorId: current.auth.user?.id }));
    } else {
      if (isSuperAdmin(before.auth.user) && !isSuperAdmin(current.auth.user)) {
        // Losing supervision invalidates cached history and in-flight reads immediately.
        dispatch(clearChatSession({ actorId: current.auth.user?.id }));
      } else if (!isSuperAdmin(before.auth.user) && isSuperAdmin(current.auth.user)) {
        dispatch(refreshChat());
      }
      if (isStrictAdmin(before.auth.user) && !isStrictAdmin(current.auth.user)) {
        dispatch(purgeChatMeetAdmin({ actorId: current.auth.user!.id }));
      }
    }
  } else if (purgingMeetAdmin) {
    const keys = new Set<QueryCacheKey>();
    Object.entries(current.chatApi.queries).forEach(([key, query]) => {
      if (query?.endpointName && meetAdminEndpoints.has(query.endpointName)) {
        keys.add(key as QueryCacheKey);
        // Preserve an already empty rejection to avoid resubscription loops.
        if (query.data !== undefined || query.status === "pending")
          dispatch(chatApi.internalActions.removeQueryResult({ queryCacheKey: key as QueryCacheKey }));
      }
    });
    dispatch(chatApi.util.getRunningQueriesThunk()).forEach((request) => {
      if (keys.has(request.queryCacheKey as QueryCacheKey)) request.abort();
    });
    dispatch(chatApi.util.getRunningMutationsThunk()).forEach((request) => {
      if (meetAdminEndpoints.has(request.arg.endpointName)) request.abort();
    });
    Object.entries(current.chatApi.mutations).forEach(([requestId, mutation]) => {
      if (mutation?.endpointName && meetAdminEndpoints.has(mutation.endpointName))
        dispatch(chatApi.internalActions.removeMutationResult({ requestId }));
    });
  } else if (purgingList) {
    const keys = new Set<QueryCacheKey>();
    const evictions = new Set<QueryCacheKey>();
    const conversationIds = new Set<string>();
    // A denied scope invalidates all its filters/pages, including pending pages
    // with no previous data. Keep the actor's other readable scopes intact.
    Object.entries(current.chatApi.queries).forEach(([queryCacheKey, query]) => {
      const args = query?.originalArgs;
      if (query?.endpointName !== "getChatConversations" || !isChatArgs(args) ||
        args.actorId !== action.payload.actorId || (args.scope ?? "mine") !== action.payload.scope) return;
      keys.add(queryCacheKey as QueryCacheKey);
      // Keep an already-empty rejection subscribed: removing it repeatedly
      // would make mounted hooks re-subscribe and loop on the same READ403.
      if (query.data !== undefined || query.status === "pending") evictions.add(queryCacheKey as QueryCacheKey);
      (query.data as CursorPage<ConversationView> | undefined)?.data.forEach(({ id }) => conversationIds.add(id));
    });
    dispatch(chatApi.util.getRunningQueriesThunk()).forEach((request) => {
      if (keys.has(request.queryCacheKey as QueryCacheKey)) request.abort();
    });
    evictions.forEach((queryCacheKey) => dispatch(chatApi.internalActions.removeQueryResult({ queryCacheKey })));
    conversationIds.forEach((conversationId) => dispatch(purgeChatConversation({ actorId: action.payload.actorId, conversationId })));
  } else if (clearing || purging) {
    const target = purging ? action.payload.conversationId : undefined;
    dispatch(chatApi.util.getRunningQueriesThunk()).forEach((request) => {
      if (!target || (isChatArgs(request.arg) && request.arg.conversationId === target)) request.abort();
    });
    dispatch(chatApi.util.getRunningMutationsThunk()).forEach((request) => {
      if (!target || (isChatArgs(request.arg.originalArgs) && request.arg.originalArgs.conversationId === target)) request.abort();
    });
    if (clearing) {
      dispatch(chatApi.util.resetApiState());
    } else {
      Object.entries(current.chatApi.queries).forEach(([queryCacheKey, query]) => {
        const args = query?.originalArgs;
        if (!isChatArgs(args) || args.actorId !== action.payload.actorId) return;
        if (args.conversationId === target) {
          dispatch(chatApi.internalActions.removeQueryResult({ queryCacheKey: queryCacheKey as QueryCacheKey }));
        } else if (query?.endpointName === "getChatConversations") {
          dispatch(chatApi.util.updateQueryData("getChatConversations", args, (page) => {
            page.data = page.data.filter((conversation) => conversation.id !== target);
          }));
        } else if (query?.endpointName === "getChatMentions") {
          dispatch(chatApi.util.updateQueryData("getChatMentions", args, (page) => {
            page.data = page.data.filter((mention) => mention.conversationId !== target);
          }));
        }
      });
      // Mutation results can also retain message text and reactions.
      Object.entries(current.chatApi.mutations).forEach(([requestId, mutation]) => {
        const data = mutation?.data as { conversationId?: string; id?: string } | undefined;
        if (data?.conversationId === target || data?.id === target) {
          dispatch(chatApi.internalActions.removeMutationResult({ requestId }));
        }
      });
    }
  } else if ([chatApi.endpoints.sendChatMessage, chatApi.endpoints.setChatReaction,
    chatApi.endpoints.createChatConversation, chatApi.endpoints.renameChatConversation,
    chatApi.endpoints.addChatMembers, chatApi.endpoints.setChatMemberRole,
    chatApi.endpoints.removeChatMember, chatApi.endpoints.markChatRead].some((endpoint) => endpoint.matchFulfilled(action))) {
    dispatch(refreshChat());
  } else if (isRejectedWithValue(action) && action.type.startsWith("chatApi/")) {
    const meta = action.meta as typeof action.meta & { arg: { originalArgs: unknown; endpointName: string } };
    const args = meta.arg.originalArgs;
    if (!isChatArgs(args) || args.actorId !== current.auth.user?.id) return result;
    const error = action.payload as { status?: number | string; data?: { code?: string } };
    if (error.status === 401 || (error.status === 403 && ["CHAT_ACCOUNT_INACTIVE", "CHAT_IMPERSONATION_FORBIDDEN"].includes(error.data?.code ?? ""))) {
      dispatch(clearChatSession({ actorId: args.actorId, blocked: true }));
    } else if (error.status === 403 && meetAdminEndpoints.has(meta.arg.endpointName)) {
      dispatch(purgeChatMeetAdmin({ actorId: args.actorId }));
    } else if (error.status === 403 && meta.arg.endpointName === "getChatConversations") {
      dispatch(purgeChatConversationList({ actorId: args.actorId, scope: args.scope ?? "mine" }));
    } else if (args.conversationId && (error.status === 404 || (error.status === 403 && conversationReads.has(meta.arg.endpointName)))) {
      dispatch(purgeChatConversation({ actorId: args.actorId, conversationId: args.conversationId }));
    }
  }
  return result;
};
